/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  // The cart-page offer lives in a Custom Liquid section, outside Dawn's
  // cart custom elements. Include its wrapper explicitly so discount-code
  // actions replace the Apply tile with the server-rendered Applied state.
  const offersSection = document
    .querySelector('[data-nv-offers]')
    ?.closest('.shopify-section[id^="shopify-section-"]');
  const offersSectionId = offersSection?.id.replace(/^shopify-section-/, '');
  if (offersSection && offersSectionId && !sections.has(offersSectionId)) {
    sections.set(offersSectionId, {
      mount: offersSection,
      extractSelector: `#${CSS.escape(offersSection.id)}`,
    });
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
function expectedCartState(actionResult) {
  const actionCart = actionResult?.cart;
  if (!actionCart) return null;

  const amount = Number(actionCart.cost?.totalAmount?.amount);
  const totalPrice = Number.isFinite(amount) ? Math.round(amount * 100) : null;
  const totalQuantity = Number(actionCart.totalQuantity);
  const discountCodes = Array.isArray(actionCart.discountCodes)
    ? actionCart.discountCodes
        .filter((discount) => discount?.applicable !== false)
        .map((discount) => String(discount?.code ?? '').trim().toUpperCase())
        .filter(Boolean)
        .sort()
    : null;

  return {
    totalPrice,
    totalQuantity: Number.isFinite(totalQuantity) ? totalQuantity : null,
    discountCodes,
  };
}

function ajaxCartMatches(cartData, expected) {
  if (!cartData || !expected) return true;
  if (expected.totalPrice !== null && cartData.total_price !== expected.totalPrice) return false;
  if (expected.totalQuantity !== null && cartData.item_count !== expected.totalQuantity) return false;

  if (expected.discountCodes) {
    const actualCodes = (cartData.cart_level_discount_applications ?? [])
      .filter((discount) => !discount.type || discount.type === 'discount_code')
      .map((discount) => String(discount.title ?? '').trim().toUpperCase())
      .filter(Boolean)
      .sort();
    if (actualCodes.join('\n') !== expected.discountCodes.join('\n')) return false;
  }

  return true;
}

async function fetchAjaxCart(cartUrl, query = '') {
  const separator = query ? '&' : '?';
  const url = `${cartUrl}.js${query}${separator}_nv=${Date.now()}`;
  return fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  }).then((response) => (response.ok ? response.json() : null));
}

async function fetchRenderedSections(cartUrl, sectionIds) {
  if (!sectionIds.length) return null;
  const query = new URLSearchParams({
    sections: sectionIds.join(','),
    _nv: String(Date.now()),
  });
  return fetch(`${cartUrl}?${query}`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  }).then((response) => (response.ok ? response.json() : null));
}

async function waitForAjaxCart(cartUrl, actionResult) {
  const expected = expectedCartState(actionResult);
  const delays = [0, 120, 220, 350, 550, 850, 1300];
  let cartData = null;

  for (const delay of delays) {
    if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
    cartData = await fetchAjaxCart(cartUrl).catch(() => null);
    if (ajaxCartMatches(cartData, expected)) break;
  }

  return cartData;
}

async function refreshDawnCartUI(actionResult) {
  const sections = collectCartSections();
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  // Standard Actions can resolve just before Shopify's Ajax cart catches up
  // with the Storefront cart. Poll the lightweight cart payload until its
  // totals/codes match the action result, then request the Liquid sections.
  // This prevents a successful discount from briefly rendering stale totals.
  const cartData = await waitForAjaxCart(cartUrl, actionResult);
  const renderedSections = sections.size
    ? await fetchRenderedSections(cartUrl, [...sections.keys()]).catch(() => null)
    : null;

  if (renderedSections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = renderedSections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ? { ...cartData, sections: renderedSections ?? undefined } : undefined,
  });
}

// Apply a code through Shopify's locale-aware Ajax Cart API. Shopify added
// the `discount` parameter to /cart/update.js in May 2025. Unlike the older
// /discount/CODE redirect, this updates the buyer's existing cart session in
// place, so Safari never leaves the product/cart page or re-enters a stale
// theme-preview route.
//
// Keep this helper global because the product offer and both cart surfaces
// use the same mutation and verification path. A successful HTTP response is
// not enough: ineligible codes can be returned without an application, so we
// confirm the requested code appears in cart_level_discount_applications before the UI
// says Applied.
function ajaxDiscountCodeTitles(cartData) {
  const cartCodes = (cartData?.cart_level_discount_applications ?? [])
    .filter((discount) => !discount?.type || discount.type === 'discount_code')
    .map((discount) => String(discount?.title ?? '').trim().toUpperCase())
    .filter(Boolean);

  // Depending on the discount's allocation method and response timing,
  // Shopify can expose a code only on the individual line allocations.
  const lineCodes = (cartData?.items ?? []).flatMap((item) =>
    (item?.line_level_discount_allocations ?? []).map((allocation) => {
      const discount = allocation?.discount_application ?? {};
      if (discount.type && discount.type !== 'discount_code') return '';
      return String(discount.title ?? '').trim().toUpperCase();
    }),
  ).filter(Boolean);

  return [...new Set([...cartCodes, ...lineCodes])];
}

function ajaxCartAsActionResult(cartData) {
  const currencyCode = cartData?.currency || window.Shopify?.currency?.active || 'INR';
  return {
    cart: {
      cost: {
        totalAmount: {
          amount: Number(cartData?.total_price ?? 0) / 100,
          currencyCode,
        },
      },
      totalQuantity: Number(cartData?.item_count ?? 0),
      discountCodes: ajaxDiscountCodeTitles(cartData).map((code) => ({ code, applicable: true })),
    },
  };
}

async function applyAjaxDiscount(code, source = 'navraee-discount') {
  const normalizedCode = String(code ?? '').trim().toUpperCase();
  if (!normalizedCode) throw new Error('Enter a valid discount code.');

  const root = window.Shopify?.routes?.root || '/';
  const currentResponse = await fetch(`${root}cart.js`, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!currentResponse.ok) throw new Error('The cart could not be checked. Please try again.');

  const currentCart = await currentResponse.json();
  if (!currentCart?.item_count) return { applied: false, empty: true, cart: currentCart };

  const discountCodes = ajaxDiscountCodeTitles(currentCart);
  if (!discountCodes.includes(normalizedCode)) discountCodes.push(normalizedCode);

  const response = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ discount: discountCodes.join(',') }),
  });

  let cartData = null;
  try {
    cartData = await response.json();
  } catch {
    cartData = null;
  }
  if (!response.ok || !cartData) {
    throw new Error(cartData?.description || 'The discount could not be applied. Please try again.');
  }

  // The update endpoint can return before the code appears in a subsequent
  // cart read. This is common for percentage codes allocated across line
  // items. Poll the lightweight cart payload briefly so “Applied” reflects
  // the committed server state rather than the first mutation response.
  let applied = ajaxDiscountCodeTitles(cartData).includes(normalizedCode);
  const verificationDelays = [100, 180, 300, 450, 700, 1000, 1400];
  for (const delay of verificationDelays) {
    if (applied) break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    const verifiedResponse = await fetch(`${root}cart.js?_nv=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).catch(() => null);
    if (!verifiedResponse?.ok) continue;
    cartData = await verifiedResponse.json().catch(() => cartData);
    applied = ajaxDiscountCodeTitles(cartData).includes(normalizedCode);
  }

  if (!applied) {
    return { applied: false, empty: false, cart: cartData };
  }

  try {
    await refreshDawnCartUI(ajaxCartAsActionResult(cartData));
  } catch (error) {
    // The cart itself is already correct. A later cart open/page view will
    // render the server state even if an optional in-page section refresh
    // failed, so do not navigate or undo the successful code application.
    console.error(`[${source}] Cart UI refresh failed.`, error);
  }

  return { applied: true, empty: false, cart: cartData };
}

window.NavraeeDiscounts = Object.freeze({ apply: applyAjaxDiscount });

let standardActionsConfigured = false;

function initStandardActions() {
  if (standardActionsConfigured) return true;
  const actions = window.Shopify?.actions;
  if (!actions?.updateCart?.configure || !actions?.openCart?.configure) return false;

  standardActionsConfigured = true;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI(result);
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });

  return true;
}

// Shopify injects Standard Actions through content_for_header. Depending on
// network timing, that async loader can attach Shopify.actions shortly after
// DOMContentLoaded, so a DOM-ready check alone is racy. Retry briefly and
// configure exactly once as soon as the API appears.
function waitForStandardActions(attempt = 0) {
  if (initStandardActions() || attempt >= 100) return;
  setTimeout(() => waitForStandardActions(attempt + 1), 100);
}

waitForStandardActions();
